/* eslint-disable react/prop-types */
import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Paper,
  Box,
  Typography,
  Avatar,
  Chip,
  Alert,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import ManageSearchOutlinedIcon from "@mui/icons-material/ManageSearchOutlined";
import {
  ExpandMore,
  ChevronRight,
  Person,
  SupervisorAccount,
  ErrorOutline,
} from "@mui/icons-material";
import ViewUserProfile from "./viewUser";
import { styled } from "@mui/system";
import { useTranslation } from "react-i18next";

const StyledTableContainer = styled(Paper)(({ theme }) => ({
  width: "100%",
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[2],
  overflow: "hidden",
  "& .MuiTable-root": {
    borderCollapse: "separate",
  },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.8)} 0%, ${alpha(theme.palette.grey[100], 0.4)} 100%)`,
  ...(theme.palette.mode === "dark" && {
    background: `linear-gradient(135deg, ${alpha(theme.palette.grey[800], 0.8)} 0%, ${alpha(theme.palette.grey[900], 0.4)} 100%)`,
  }),
  "& .MuiTableCell-head": {
    fontWeight: 700,
    fontSize: "0.875rem",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    borderBottom: `2px solid ${theme.palette.divider}`,
    padding: theme.spacing(2, 3),
  },
}));

const StyledTableRow = styled(TableRow)(({ theme, level }) => ({
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    transform: "translateY(-1px)",
    boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
  },
  "& .MuiTableCell-root": {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
    padding: theme.spacing(2, 3),
    fontSize: "0.875rem",
  },
  ...(level > 0 && {
    backgroundColor: alpha(theme.palette.grey[50], 0.3),
    ...(theme.palette.mode === "dark" && {
      backgroundColor: alpha(theme.palette.grey[800], 0.3),
    }),
  }),
}));

const UserTree = ({ users, currentUserId }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const checkSelfManagedUsers = (users) => {
    return users.some((user) => user.id === user.manager_id);
  };

  const buildUserTree = (users, currentUserId) => {
    if (checkSelfManagedUsers(users)) {
      return {
        error: t("userTree.selfManagedError"),
      };
    }

    const userMap = new Map(
      users?.map((user) => [user.id, { ...user, subordinates: [] }])
    );
    const rootUser = userMap.get(currentUserId);
    if (!rootUser) return [];

    users?.forEach((user) => {
      if (
        user.manager_id &&
        user.manager_id !== user.id &&
        userMap.has(user.manager_id)
      ) {
        userMap.get(user.manager_id).subordinates.push(userMap.get(user.id));
      }
    });

    return rootUser.subordinates.length > 0 ? [rootUser] : [];
  };

  const userTree = buildUserTree(users, currentUserId);

  if (userTree.error) {
    return (
      <Alert
        severity="error"
        icon={<ErrorOutline />}
        sx={{
          borderRadius: 3,
          border: "1px solid",
          borderColor: alpha(theme.palette.error.main, 0.2),
          "& .MuiAlert-message": {
            display: "flex",
            alignItems: "center",
            gap: 1,
          },
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {userTree.error}
        </Typography>
      </Alert>
    );
  }

  if (userTree.length === 0) {
    return (
      <Paper
        sx={{
          p: 6,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          textAlign: "center",
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.5)} 0%, ${alpha(theme.palette.grey[100], 0.2)} 100%)`,
          ...(theme.palette.mode === "dark" && {
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[800], 0.5)} 0%, ${alpha(theme.palette.grey[900], 0.2)} 100%)`,
          }),
        }}
      >
        <Person
          sx={{
            fontSize: 64,
            color: "text.disabled",
            mb: 2,
          }}
        />
        <Typography
          variant="h6"
          sx={{
            color: "text.secondary",
            fontWeight: 600,
            mb: 1,
          }}
        >
          {t("userTree.noTeamMembersText")}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.disabled",
          }}
        >
          No team members are currently assigned to you
        </Typography>
      </Paper>
    );
  }

  return (
    <StyledTableContainer>
      <Table aria-label={t("userTree.tableAria")}>
        <StyledTableHead>
          <TableRow>
            <TableCell width="35%">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Person sx={{ fontSize: 18 }} />
                {t("userTree.nameHeader")}
              </Box>
            </TableCell>
            <TableCell width="25%">{t("userTree.surnameHeader")}</TableCell>
            <TableCell width="25%">{t("userTree.usernameHeader")}</TableCell>
            <TableCell width="15%" align="center">
              {t("userTree.actionsHeader")}
            </TableCell>
          </TableRow>
        </StyledTableHead>
        <TableBody>
          {userTree.map((user) => (
            <UserRow key={user.id} user={user} level={0} />
          ))}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};

const UserRow = ({ user, level }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [expanded, setExpanded] = useState(true);
  const [viewProfile, setViewProfile] = useState(false);

  const handleToggle = () => setExpanded(!expanded);
  const handleUserDetails = () => setViewProfile(true);

  const getInitials = (user) => {
    const firstName = user?.first_name || "";
    const lastName = user?.last_name || "";
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  return (
    <>
      <StyledTableRow level={level}>
        <TableCell>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              pl: level * 11, // Avatar-based indentation: each level indented by avatar width + gaps (32px button + 16px gap + 40px avatar + 16px gap = 104px = 13 * 8px)
              gap: 2,
            }}
          >
            {user?.subordinates.length > 0 && (
              <IconButton
                size="small"
                onClick={handleToggle}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.2s ease",
                  width: 32,
                  height: 32,
                }}
                aria-expanded={expanded}
                aria-label={t("userTree.toggleAria", {
                  action: expanded
                    ? t("userTree.collapseText")
                    : t("userTree.expandText"),
                  username: user.username || t("userTree.unknownUsername"),
                })}
              >
                {expanded ? (
                  <ExpandMore sx={{ fontSize: 18, color: "primary.main" }} />
                ) : (
                  <ChevronRight sx={{ fontSize: 18, color: "primary.main" }} />
                )}
              </IconButton>
            )}

            <Avatar
              src={user?.avatar?.url || ""}
              sx={{
                width: 40,
                height: 40,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
                fontWeight: 600,
                fontSize: "0.875rem",
                border: "2px solid",
                borderColor: alpha(theme.palette.primary.main, 0.2),
              }}
            >
              {getInitials(user)}
            </Avatar>

            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                }}
              >
                {user.first_name || t("userTree.unknownName")}
              </Typography>
              {level === 0 && (
                <Chip
                  icon={<SupervisorAccount sx={{ fontSize: 14 }} />}
                  label="Manager"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.75rem",
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    color: "info.main",
                    "& .MuiChip-icon": {
                      color: "info.main",
                    },
                  }}
                />
              )}
            </Box>
          </Box>
        </TableCell>
        <TableCell>
          <Typography
            variant="body2"
            sx={{
              color: "text.primary",
              fontWeight: 500,
            }}
          >
            {user.last_name || t("userTree.unknownName")}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontFamily: "monospace",
              fontSize: "0.8125rem",
            }}
          >
            {user.username || t("userTree.unknownUsername")}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Tooltip title={t("userTree.detailsTooltip")} placement="top">
            <IconButton
              onClick={handleUserDetails}
              sx={{
                bgcolor: alpha(theme.palette.success.main, 0.08),
                "&:hover": {
                  bgcolor: alpha(theme.palette.success.main, 0.12),
                  transform: "scale(1.1)",
                },
                transition: "all 0.2s ease",
                width: 36,
                height: 36,
              }}
              aria-label={t("userTree.detailsAria", {
                username: user.username || t("userTree.unknownUsername"),
              })}
            >
              <ManageSearchOutlinedIcon
                sx={{
                  fontSize: 18,
                  color: "success.main",
                }}
              />
            </IconButton>
          </Tooltip>
        </TableCell>
      </StyledTableRow>
      {expanded &&
        user?.subordinates.map((sub) => (
          <UserRow key={sub.id} user={sub} level={level + 1} />
        ))}
      {viewProfile && (
        <ViewUserProfile
          user={user}
          open={viewProfile}
          onClose={() => setViewProfile(false)}
        />
      )}
    </>
  );
};

export default UserTree;
